import api from './api';

const base = (programId) => `/api/programs/${programId}`;

// Queries
export const getProgramMembers = (programId, { status, role } = {}, options = {}) => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (role) params.append('role', role);
  const qs = params.toString();
  return api.get(`${base(programId)}/members${qs ? `?${qs}` : ''}`, options);
};

export const getProgramFollowers = (programId, params = {}) => {
  const usp = new URLSearchParams();
  if (params.status) usp.append('status', params.status);
  if (params.role) usp.append('role', params.role);
  if (params.page) usp.append('page', params.page);
  if (params.limit) usp.append('limit', params.limit);
  const qs = usp.toString();
  return api.get(`${base(programId)}/followers${qs ? `?${qs}` : ''}`);
};

export const getProgramApplicants = (programId, params = {}) => {
  const usp = new URLSearchParams();
  if (params.review_status) usp.append('review_status', params.review_status);
  if (params.page) usp.append('page', params.page);
  if (params.limit) usp.append('limit', params.limit);
  const qs = usp.toString();
  return api.get(`${base(programId)}/applicants${qs ? `?${qs}` : ''}`);
};

// Mutations
export const followProgram = (programId, payload = {}) => api.post(`${base(programId)}/follow`, payload);

export const unfollowProgram = (programId) => api.post(`${base(programId)}/unfollow`);

export const getFollowingStatus = (programId) => api.get(`${base(programId)}/following-status`);

export const waitlistProgram = (programId, payload = {}) => api.post(`${base(programId)}/waitlist`, payload);

export const applyToProgram = (programId, payload) => api.post(`${base(programId)}/apply`, payload);

// Fixed signature per backend doc (approve by applicationId, not userId)
export const approveApplication = (programId, applicationId, payload) =>
  api.post(`${base(programId)}/applications/${applicationId}/approve`, payload);

export const declineApplication = (programId, applicationId, payload = {}) =>
  api.post(`${base(programId)}/applications/${applicationId}/decline`, payload);

// Updated endpoints to PUT per backend doc
export const setMemberRole = (programId, userId, role, notes) =>
  api.put(`${base(programId)}/members/${userId}/role`, { role, notes });

export const setMemberStatus = (programId, userId, status, notes = '') =>
  api.put(`${base(programId)}/members/${userId}/status`, { 
    status, 
    notes: notes || '' // Ensure notes is always a string
  });

export const removeMember = (programId, userId, notes) =>
  api.del(`${base(programId)}/members/${userId}`, { notes });

export const notify = (programId, payload) => api.post(`${base(programId)}/notify`, payload);

// Program Capacity Management
export const updateProgramCapacity = (programId, capacitySettings) =>
  api.put(`${base(programId)}/capacity`, capacitySettings);

export const getProgramCapacityStatus = (programId) =>
  api.get(`${base(programId)}/capacity/status`);

export const enrollParticipant = (programId, userId, source = 'manual') =>
  api.post(`${base(programId)}/enroll`, { userId, source });

export const removeParticipant = (programId, userId, reason = 'removal', notes = '') =>
  api.del(`${base(programId)}/participants/${userId}`, { reason, notes });

// Backward compatibility helpers for existing frontend code
export const getProgramCapacity = (programId) => getProgramCapacityStatus(programId);

export const checkProgramCapacity = async (programId) => {
  const status = await getProgramCapacityStatus(programId);
  return {
    canEnroll: status.canAcceptRegistrations,
    isAtCapacity: status.isAtCapacity,
    waitlistAvailable: status.waitlistEnabled && !status.isAtCapacity,
    spotsLeft: status.availableSpots,
    utilizationPercent: status.capacityUtilization
  };
};

// ============================================
// EXPLORE MODE - Programs Discovery Functions
// ============================================

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

/**
 * Get all programs for a specific club
 * @param {string} clubId - The club ID
 * @returns {Promise<Array>} Array of programs
 */
export const getProgramsByClub = async (clubId) => {
  try {
    const response = await fetch(`${backendUrl}/api/programs/club/${clubId}`);
    if (!response.ok) {
      // Return empty array for 404 (club has no programs) or other errors
      return [];
    }
    return await response.json();
  } catch (error) {
    // Network errors - return empty array
    return [];
  }
};

/**
 * Get a single program by ID
 * @param {string} programId - The program ID
 * @returns {Promise<Object|null>} Program object or null
 */
export const getProgramById = async (programId) => {
  try {
    const response = await fetch(`${backendUrl}/api/programs/${programId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch program');
    }
    return await response.json();
  } catch (error) {
    console.error(`Error fetching program ${programId}:`, error);
    return null;
  }
};

/**
 * Get programs for multiple clubs in parallel
 * @param {Array<string>} clubIds - Array of club IDs
 * @returns {Promise<Object>} Map of clubId -> programs array
 */
export const getProgramsForClubs = async (clubIds) => {
  const programsMap = {};
  
  const results = await Promise.allSettled(
    clubIds.map(async (clubId) => {
      const programs = await getProgramsByClub(clubId);
      return { clubId, programs };
    })
  );

  results.forEach((result) => {
    if (result.status === 'fulfilled') {
      programsMap[result.value.clubId] = result.value.programs;
    }
  });

  return programsMap;
};

/**
 * Get capacity status for multiple programs in parallel
 * @param {Array<string>} programIds - Array of program IDs
 * @returns {Promise<Object>} Map of programId -> capacity status
 */
export const getCapacityForPrograms = async (programIds) => {
  const capacityMap = {};
  
  const results = await Promise.allSettled(
    programIds.map(async (programId) => {
      try {
        const status = await getProgramCapacityStatus(programId);
        return { programId, status };
      } catch (error) {
        // Return default status if capacity check fails
        return { 
          programId, 
          status: { 
            availableSpots: null, 
            isAtCapacity: false, 
            waitlistEnabled: false 
          } 
        };
      }
    })
  );

  results.forEach((result) => {
    if (result.status === 'fulfilled') {
      capacityMap[result.value.programId] = result.value.status;
    }
  });

  return capacityMap;
};

/**
 * Filter programs based on explore filters
 * @param {Array} programs - Array of programs to filter
 * @param {Object} filters - Filter criteria
 * @param {Object} capacityMap - Map of programId -> capacity status (optional)
 * @returns {Array} Filtered programs
 */
export const filterPrograms = (programs, filters, capacityMap = {}) => {
  if (!Array.isArray(programs)) return [];
  
  return programs.filter((program) => {
    // Sport filter
    if (filters.sport && filters.sport !== 'All') {
      if (program.sport !== filters.sport) return false;
    }

    // Age filter
    if (filters.ageGroup && filters.ageGroup !== 'all') {
      const { minAge, maxAge } = getAgeRangeFromFilter(filters.ageGroup);
      const programMinAge = program.age?.min || 0;
      const programMaxAge = program.age?.max || 100;
      
      // Check if age ranges overlap
      if (programMaxAge < minAge || programMinAge > maxAge) return false;
    }

    // Price filter
    if (filters.priceRange && filters.priceRange !== 'all') {
      const { min, max } = getPriceRangeFromFilter(filters.priceRange);
      const programCost = getProgramCostAmount(program);
      
      if (programCost < min || programCost > max) return false;
    }

    // Start date filter
    if (filters.startDate && filters.startDate !== 'all') {
      const { start, end } = getStartDateRangeFromFilter(filters.startDate);
      if (start && end && program.schedule?.startDate) {
        const programStart = new Date(program.schedule.startDate);
        if (programStart < start || programStart > end) return false;
      }
    }

    // Search query filter
    if (filters.searchQuery && filters.searchQuery.trim()) {
      const query = filters.searchQuery.toLowerCase().trim();
      const searchableText = [
        program.name,
        program.sport,
        program.programDetails,
        program.category?.name
      ].filter(Boolean).join(' ').toLowerCase();
      
      if (!searchableText.includes(query)) return false;
    }

    // Has openings filter
    if (filters.hasOpenings) {
      const capacity = capacityMap[program._id];
      if (capacity && capacity.isAtCapacity && !capacity.waitlistEnabled) {
        return false;
      }
    }

    // Only show active programs
    if (program.active === false) return false;

    return true;
  });
};

// Helper functions for filter ranges
const getAgeRangeFromFilter = (filterValue) => {
  const ageMap = {
    'toddler': { minAge: 2, maxAge: 4 },
    'u6': { minAge: 5, maxAge: 6 },
    'u8': { minAge: 7, maxAge: 8 },
    'u10': { minAge: 9, maxAge: 10 },
    'u12': { minAge: 11, maxAge: 12 },
    'u14': { minAge: 13, maxAge: 14 },
    'u16': { minAge: 15, maxAge: 16 },
    'u18': { minAge: 17, maxAge: 18 },
    'adult': { minAge: 18, maxAge: 65 },
    'senior': { minAge: 50, maxAge: 100 }
  };
  return ageMap[filterValue] || { minAge: 0, maxAge: 100 };
};

const getPriceRangeFromFilter = (filterValue) => {
  const priceMap = {
    'free': { min: 0, max: 0 },
    'under50': { min: 0, max: 50 },
    '50to100': { min: 50, max: 100 },
    '100to200': { min: 100, max: 200 },
    '200to500': { min: 200, max: 500 },
    'over500': { min: 500, max: Infinity }
  };
  return priceMap[filterValue] || { min: 0, max: Infinity };
};

const getStartDateRangeFromFilter = (filterValue) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  switch (filterValue) {
    case 'thisMonth': {
      const start = new Date(currentYear, currentMonth, 1);
      const end = new Date(currentYear, currentMonth + 1, 0);
      return { start, end };
    }
    case 'nextMonth': {
      const start = new Date(currentYear, currentMonth + 1, 1);
      const end = new Date(currentYear, currentMonth + 2, 0);
      return { start, end };
    }
    case 'next3Months': {
      const start = now;
      const end = new Date(currentYear, currentMonth + 3, now.getDate());
      return { start, end };
    }
    case 'spring': {
      const year = currentMonth > 4 ? currentYear + 1 : currentYear;
      return { start: new Date(year, 2, 1), end: new Date(year, 5, 0) };
    }
    case 'summer': {
      const year = currentMonth > 7 ? currentYear + 1 : currentYear;
      return { start: new Date(year, 5, 1), end: new Date(year, 8, 0) };
    }
    case 'fall': {
      const year = currentMonth > 10 ? currentYear + 1 : currentYear;
      return { start: new Date(year, 8, 1), end: new Date(year, 11, 0) };
    }
    default:
      return { start: null, end: null };
  }
};

const getProgramCostAmount = (program) => {
  if (!program?.cost) return 0;

  // Check registration fees first
  const regFees = program.cost.registrationFees;
  if (regFees && regFees.length > 0 && regFees[0].amount) {
    return parseFloat(regFees[0].amount) || 0;
  }

  // Check fee structure
  const feeStructure = program.cost.feeStructure;
  if (feeStructure && feeStructure.length > 0 && feeStructure[0].amount) {
    return parseFloat(feeStructure[0].amount) || 0;
  }

  return 0;
};

